import React, { useCallback, useContext, useEffect } from 'react';
import * as ReactDOM from 'react-dom/client';

import { ThemeProvider } from '@emotion/react'
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { createBrowserRouter, RouterProvider, Route, Routes, useLocation, useNavigate, Navigate, RouteObject, useRoutes, redirect } from 'react-router-dom';
import theme from 'utils/Theme';
import {
  defaultTheme,   // Default theme
  darkTheme,      // Dark theme
} from '@ant-design/compatible';
import { ConfigProvider } from 'antd';

// Initialize languages
import './locales/i18n';

import * as serviceWorker from './serviceWorker';

import { AuthServiceInstance } from 'services/authService';
import { AuthStoreContext } from 'store/AuthStore';

import AnswerSurvey from 'routes/AnswerSurvey';
import CeoEnrollmentForm from 'routes/CeoEnrollmentForm';
import CeoSurvey from 'routes/CeoSurvey';
import Login from 'routes/Login';
import NotFound from 'routes/NotFound';
import Success from 'routes/Success';
import Profile from 'routes/Dashboard/Profile';
import UserManagement from 'routes/Dashboard/UserManagement';
import AddUser from 'routes/Dashboard/AddUser';
import Diagnostic from 'routes/Dashboard/Diagnostic';
import EnrollmentFormInvitation from 'routes/Dashboard/EnrollmentFormInvitation';
import CreateEnrollmentForm from 'routes/Dashboard/CreateEnrollmentForm';
import EnrollmentForms from 'routes/Dashboard/EnrollmentForms';
import CreateSurvey from 'routes/Dashboard/CreateSurvey';
import Survey from 'routes/Dashboard/Survey';
import DashboardLayout from 'components/layout/Dashboard';

// Create a client
const queryClient = new QueryClient();

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const AuthStore = useContext(AuthStoreContext);
  const { isAuthenticated, finishedPersisting, userType } = AuthStore;

  const verifyValidToken = useCallback(async () => {
    try {
      await AuthServiceInstance.verify();
    } catch (error) {
      AuthStore.logout();
    }
  }, [AuthStore]);

  useEffect(() => {
    if (finishedPersisting) {
      if (
        location.pathname.includes('answer') ||
        location.pathname.includes('not-found') ||
        location.pathname.includes('success')
      ) {
        return;
      }

      if (isAuthenticated && !location.pathname.includes('dashboard')) {
        navigate('/dashboard/enrollment-forms');

        return;
      }

      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
    }
  }, [
    isAuthenticated,
    location.pathname,
    finishedPersisting,
    verifyValidToken,
  ]);

  useEffect(() => { verifyValidToken(); }, [verifyValidToken]);

  return useRoutes([
    // PUBLIC_ROUTES
    {
      // ANSWER_SURVEY
      path: '/answer-survey',
      Component: AnswerSurvey
    },
    {
      //CEO_SURVEY
      path: '/answer-ceoSurvey/:id',
      Component: CeoSurvey
    },
    {
      //CEO_ENROLLMENT_FORM
      path: '/answer-enrollmentForm/:id',
      Component: CeoEnrollmentForm
    },
    {
      //NOT_FOUND
      path: '/not-found',
      Component: NotFound
    },
    {
      //SUCCESS
      path: '/success',
      Component: Success
    },
    {
      //LOGIN
      path: '/login',
      element: <Login />
    },

    // SUPER_ADMIN_ROUTES
    { 
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        {
          index: true,
          element: <Navigate to={
            userType === 'SUPER_ADMIN' 
              ? '/dashboard/user-management'
              : '/dashboard/enrollment-forms'
            } />,
        },
        // ADMINS
        {
          //SURVEY
          path: 'survey',
          // element: userType === 'SUPER_ADMIN' ? <Navigate to="/dashboard/user-management" /> : <Survey />,
          children: [
            {
              //LANDING
              index: true,
              Component: Survey,
            },
            {
              //CREATE
              path: 'create',
              Component: CreateSurvey,
            },
          ],
        },
        {
          //ENROLLMENT_FORMS
          path: 'enrollment-forms',
          children: [
            {
              //LANDING
              index: true,
              Component: EnrollmentForms,
            },
            {
              //CREATE
              path: 'create',
              Component: CreateEnrollmentForm,
            },
            {
              //INVITE
              path: 'invite',
              Component: EnrollmentFormInvitation,
            },
          ],
        },
        {
          //DIAGNOSTIC
          path: 'diagnostic/:enrollmentId',
          // element: userType === 'SUPER_ADMIN' ? <Navigate to="/dashboard/user-management" /> : <Diagnostic/>,
          Component: Diagnostic,
        },
        {
          //PROFILE
          path: 'profile',
          Component: Profile,
        },

        // SUPER_ADMIN
        {
          //USER_MANAGEMENT
          path: 'user-management',
          element: userType === 'SUPER_ADMIN' ? <UserManagement /> : <Navigate to="/dashboard/enrollment-forms" />,
            children: [
              {
                //LANDING FIXME: require?
                index: true,
                Component: UserManagement,
              },
              {
                //CREATE
                path: 'add',
                Component: AddUser,
              },
            ],
        },
      ]
    }
  ]);
};


const router = createBrowserRouter([
  { path: '*', Component: App } // splat route to redirect all to App
]);
ReactDOM.createRoot(document.getElementById('root')!)
  .render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#194261',
              colorInfo: '#194261'
            }
          }}
        >
          <RouterProvider router={router}/>
        </ConfigProvider>
        {/* <ThemeProvider theme={theme}> */}
        {/* </ThemeProvider> */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>
  );

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
